import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';

interface MediaFile {
  id: number;
  filename: string;
  original_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

const MediaManager: React.FC = () => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMediaFiles();
  }, []);

  const fetchMediaFiles = async () => {
    try {
      const response = await api.get('/cms/media');
      setMediaFiles(response.data);
    } catch (error: any) {
      console.error('Error fetching media files:', error);
      alert('Error loading media files: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(e.target.files);
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const formData = new FormData();
        formData.append('file', selectedFiles[i]);
        
        console.log('Uploading file:', selectedFiles[i].name, 'Size:', selectedFiles[i].size);
        
        const response = await api.post('/cms/media/upload', formData);
        
        console.log('Upload response:', response.data);
      }
      
      setShowUploadModal(false);
      setSelectedFiles(null);
      fetchMediaFiles();
      alert('Files uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading files:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert('Error uploading files: ' + errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await api.delete(`/cms/media/${id}`);
        fetchMediaFiles();
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert('Error deleting file: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return 'fas fa-image';
    if (fileType.includes('pdf')) return 'fas fa-file-pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'fas fa-file-word';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fas fa-file-excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'fas fa-file-powerpoint';
    if (fileType.includes('video/')) return 'fas fa-file-video';
    if (fileType.includes('audio/')) return 'fas fa-file-audio';
    return 'fas fa-file';
  };

  const filteredFiles = mediaFiles.filter(file => {
    const matchesFilter = filter === 'all' || 
      (filter === 'images' && file.file_type.startsWith('image/')) ||
      (filter === 'documents' && file.file_type.startsWith('application/'));
    
    const matchesSearch = file.original_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return <div className="loading">Loading media files...</div>;
  }

  return (
    <div className="media-manager">
      <div className="media-header">
        <div className="header-content">
          <h1>Media Management</h1>
          <p>Upload, organize, and manage your media files</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            <i className="fas fa-upload"></i>
            Upload Files
          </button>
        </div>
      </div>

      <div className="media-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search media files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Files
          </button>
          <button 
            className={`filter-btn ${filter === 'images' ? 'active' : ''}`}
            onClick={() => setFilter('images')}
          >
            Images
          </button>
          <button 
            className={`filter-btn ${filter === 'documents' ? 'active' : ''}`}
            onClick={() => setFilter('documents')}
          >
            Documents
          </button>
        </div>
      </div>

      <div className="media-stats">
        <div className="stat-item">
          <span className="stat-number">{mediaFiles.length}</span>
          <span className="stat-label">Total Files</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{mediaFiles.filter(f => f.file_type.startsWith('image/')).length}</span>
          <span className="stat-label">Images</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{mediaFiles.filter(f => f.file_type.startsWith('application/')).length}</span>
          <span className="stat-label">Documents</span>
        </div>
      </div>

      <div className="media-grid">
        {filteredFiles.map((file) => (
          <div key={file.id} className="media-card">
            <div className="media-preview">
              {file.file_type.startsWith('image/') ? (
                <img 
                  src={getImageUrl(file.file_path)} 
                  alt={file.original_name}
                  className="media-image"
                />
              ) : (
                <div className="media-icon">
                  <i className={getFileIcon(file.file_type)}></i>
                </div>
              )}
            </div>
            <div className="media-info">
              <h4 className="media-name" title={file.original_name}>
                {file.original_name}
              </h4>
              <div className="media-details">
                <span className="file-size">{formatFileSize(file.file_size)}</span>
                <span className="file-type">{file.file_type.split('/')[1]?.toUpperCase()}</span>
              </div>
              <div className="media-actions">
                <button 
                  className="btn-view"
                  onClick={() => window.open(getImageUrl(file.file_path), '_blank')}
                  title="View File"
                >
                  <i className="fas fa-eye"></i>
                </button>
                <button 
                  className="btn-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(getImageUrl(file.file_path));
                    alert('File URL copied to clipboard!');
                  }}
                  title="Copy URL"
                >
                  <i className="fas fa-copy"></i>
                </button>
                <button 
                  className="btn-delete"
                  onClick={() => handleDelete(file.id)}
                  title="Delete File"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredFiles.length === 0 && (
        <div className="empty-state">
          <i className="fas fa-images"></i>
          <h3>No media files found</h3>
          <p>Upload some files to get started</p>
          <button 
            className="btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Files
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Upload Media Files</h3>
              <button 
                className="btn-close"
                onClick={() => setShowUploadModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="upload-area">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="upload-zone">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <h4>Choose files to upload</h4>
                  <p>Drag and drop files here or click to browse</p>
                  <p className="upload-hint">Supports: JPG, PNG, GIF, PDF, DOC, DOCX, MP4, MP3</p>
                </label>
                {selectedFiles && (
                  <div className="selected-files">
                    <div className="selected-files-header">
                      <h4>Selected Files ({selectedFiles.length}):</h4>
                      <button 
                        className="btn-clear"
                        onClick={() => {
                          setSelectedFiles(null);
                          const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        title="Clear Selection"
                      >
                        <i className="fas fa-times"></i>
                        Clear
                      </button>
                    </div>
                    <div className="file-previews">
                      {Array.from(selectedFiles).map((file, index) => (
                        <div key={index} className="file-preview-item">
                          <div className="file-preview">
                            {file.type.startsWith('image/') ? (
                              <img 
                                src={URL.createObjectURL(file)} 
                                alt={file.name}
                                className="preview-image"
                              />
                            ) : (
                              <div className="preview-icon">
                                <i className={getFileIcon(file.type)}></i>
                              </div>
                            )}
                          </div>
                          <div className="file-info">
                            <div className="file-name" title={file.name}>{file.name}</div>
                            <div className="file-details">
                              <span className="file-size">{formatFileSize(file.size)}</span>
                              <span className="file-type">{file.type.split('/')[1]?.toUpperCase()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleUpload}
                disabled={!selectedFiles || uploading}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i>
                    Upload Files
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManager;
